import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, IconButton, Pill, fmtDataISO, type Column } from "@atlas/ui/web";
import { atlas } from "../../../../lib/sdk";
import type { BancoTabela } from "@atlas/sdk";

const fmtPct = (n: number) => `${(n * 100).toFixed(2)}%`;
// fmtDate local removido — usava new Date(iso).toLocaleDateString que
// interpreta "YYYY-MM-DD" como UTC (off-by-one em BRT). fmtDataISO faz slice
// direto do regex, sem instanciar Date. Cliente 28/07/2026.
const fmtDate = (iso?: string) => fmtDataISO(iso);

export function BancoTabelaEmprestimosLista() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ["banco", "tabelas"], queryFn: () => atlas.banco.listTabelas() });
  // Segue o convenio ATIVO selecionado no topo (mesmo padrao da carteira,
  // commit 12338fa): a lista mostra so as tabelas da prefeitura/convenio
  // selecionado, nunca de outras prefeituras. Cliente pediu 30/07/2026.
  // queryKey ["banco","convenios"] e' o mesmo do layout — trocar o convenio la
  // invalida ["banco"] e re-filtra aqui automaticamente.
  const convQ = useQuery({ queryKey: ["banco", "convenios"], queryFn: () => atlas.banco.convenios() });
  const activeId = convQ.data?.activeId;
  const tabelasFiltradas = (data.data?.tabelas ?? []).filter((t) => !activeId || t.convenioId === activeId);
  const remove = useMutation({
    mutationFn: (id: string) => atlas.banco.removerTabela(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["banco", "tabelas"] });
      qc.invalidateQueries({ queryKey: ["banco", "tabela", id] });
      qc.invalidateQueries({ queryKey: ["servidor", "ofertas"] });
    },
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => atlas.banco.reativarTabela(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["banco", "tabelas"] });
      qc.invalidateQueries({ queryKey: ["banco", "tabela", id] });
      qc.invalidateQueries({ queryKey: ["servidor", "ofertas"] });
    },
  });

  const columns: Column<BancoTabela>[] = [
    {
      key: "ativo",
      header: "Situação",
      render: (t) => <Pill variant={t.ativo ? "averbado" : "expirado"}>{t.ativo ? "Ativo" : "Inativo"}</Pill>,
    },
    { key: "criadoEm", header: "Data Cadastro", render: (t) => fmtDate(t.criadoEm) },
    { key: "vigenciaInicio", header: "Início Vigência", render: (t) => fmtDate(t.vigenciaInicio) },
    // "Aberta" em vez de "—" quando o banco cadastrou tabela sem fim (convenio
    // sem vigencia fim). "—" ficava ambiguo: parecia bug ("nao esta sendo
    // preenchida").
    { key: "vigenciaFim", header: "Fim Vigência", render: (t) => t.vigenciaFim ? fmtDate(t.vigenciaFim) : "Aberta" },
    { key: "convenio", header: "Convênio" },
    { key: "taxa", header: "Taxa a.m.", render: (t) => fmtPct(t.taxaAm ?? t.taxaMaxAm ?? 0) },
    { key: "prazoMaxMeses", header: "Prazo max" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Cadastros
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Tabela de Empréstimos</h1>
        </div>
        <Button onClick={() => nav("novo")}>+ Inserir</Button>
      </header>

      <DataTable
        columns={columns}
        rows={tabelasFiltradas}
        rowKey={(t) => t.id}
        loading={data.isLoading || convQ.isLoading}
        emptyState="Nenhuma tabela cadastrada. Clique em '+ Nova tabela' pra publicar suas taxas."
        actions={(t) => (
          <>
            <IconButton title="Editar" onClick={() => nav(t.id)}>✎</IconButton>
            {t.ativo ? (
              <IconButton
                title="Desativar"
                danger
                onClick={() => {
                  if (confirm(`Desativar tabela ${t.id}?\n\nEla para de aparecer nas simulações, mas nada é apagado — você pode reativar depois.`)) remove.mutate(t.id);
                }}
              >
                ⏸
              </IconButton>
            ) : (
              <IconButton title="Reativar" onClick={() => reactivate.mutate(t.id)}>▶</IconButton>
            )}
          </>
        )}
      />
    </div>
  );
}
